// Per-merchant email templates.
//
// go-pim does NOT store or render these — it proxies notification-service's
// template admin, which is where the rendering and its HTML auto-escaping live.
// That is not a preference: notification-service's send API has no body field,
// so a finished email cannot be handed to it, and adding one would let any
// API-key holder emit arbitrary HTML from their tenant's authenticated sending
// domain.
//
// Practical consequence for this client: the bodies below are TEMPLATES, not
// rendered output. `{{ .customer_name }}` and friends are interpolated by
// notification-service at send time, with html_body auto-escaped.
import { apiClient } from "./client";

/** doc_type is go-pim's vocabulary; the proxy maps it to a template_type. */
export type EmailDocType = "receipt" | "invoice";

export interface EmailTemplate {
  doc_type: EmailDocType;
  subject: string;
  html_body: string;
  plain_body: string;
  /**
   * Whether a per-merchant row exists yet. False means this merchant currently
   * falls back to the SHARED default template — a real state, and distinct from
   * "a row exists but its fields are blank", which is why the API reports it
   * rather than leaving it to be inferred from empty strings.
   */
  configured: boolean;
  /** Read-only here: channel wiring is provisioning, owned by the other service. */
  channel_id: number;
}

export interface EmailTemplatesResponse {
  templates: EmailTemplate[];
  /**
   * The brand these templates belong to. Echoed by the API so the operator can
   * SEE which merchant they are editing — editing the wrong one is the failure
   * mode that matters here, since brand is not part of notification-service's
   * authorization scope.
   */
  brand: string;
}

/** listEmailTemplates — GET /v1/companies/:id/email-templates */
export async function listEmailTemplates(
  companyId: string,
): Promise<EmailTemplatesResponse> {
  const resp = await apiClient.get<EmailTemplatesResponse>(
    `/v1/companies/${companyId}/email-templates`,
  );
  return { brand: resp.data.brand, templates: resp.data.templates ?? [] };
}

/** saveEmailTemplate — PUT /v1/companies/:id/email-templates/:docType */
export async function saveEmailTemplate(
  companyId: string,
  docType: EmailDocType,
  body: Pick<
    EmailTemplate,
    "subject" | "html_body" | "plain_body" | "channel_id"
  >,
): Promise<EmailTemplate> {
  const resp = await apiClient.put<EmailTemplate>(
    `/v1/companies/${companyId}/email-templates/${docType}`,
    {
      doc_type: docType,
      subject: body.subject,
      html_body: body.html_body,
      plain_body: body.plain_body,
      configured: true,
      channel_id: body.channel_id,
    },
  );
  return resp.data;
}

/**
 * The variables notification-service's receipt template is sent, so the editor
 * can show what is available rather than leaving the operator to guess.
 *
 * Mirrors receiptEmailVars in go-pim's internal/pimgrpc/document.go. Optional
 * ones are OMITTED rather than sent empty, so a template must guard them with
 * {{ if }} — which is why each is marked here.
 */
export const TEMPLATE_VARIABLES: {
  name: string;
  note: string;
  always: boolean;
}[] = [
  { name: "doc_type", note: "Receipt or Invoice", always: true },
  { name: "number", note: "e.g. RCPT-000001", always: true },
  { name: "issued_at", note: "RFC3339 timestamp", always: true },
  { name: "issuer_name", note: "the merchant's legal name", always: true },
  { name: "amount", note: "formatted, e.g. ₹1,234.56", always: true },
  { name: "currency", note: "e.g. INR", always: true },
  { name: "payment_ref", note: "the settled payment reference", always: true },
  { name: "customer_name", note: "omitted when unknown", always: false },
  { name: "tax_amount", note: "omitted when there is no tax", always: false },
  { name: "subtotal", note: "omitted when there is no tax", always: false },
  { name: "line_items", note: "list; omitted when empty", always: false },
  { name: "verify_url", note: "the public /r/{slug} link", always: false },
];
